import {
  ApiResponse,
  PaginatedResponse,
  Case,
  Document,
  Letter,
  CreateCaseForm,
  CreateCaseResponse,
  ProcessingStats,
} from '@/types';
import { apiClient } from './client';
import { FolderSuggestionResponse } from '@/types/document';

// Simple in-memory cache for visa types
let _visaTypesCache: string[] | null = null;
let _visaTypesPromise: Promise<string[]> | null = null;

export class ApiService {
  // Auth endpoints
  static async login(
    email: string,
    password: string
  ): Promise<ApiResponse<{ user: { id: string; email: string; name: string; role: string }; token: string }>> {
    return apiClient.post('/auth/login', { email, password });
  }

  static async logout(): Promise<ApiResponse> {
    return apiClient.post('/auth/signout', {});
  }

  static async getAuthStatus(): Promise<any> {
    return apiClient.get('/auth/status');
  }

  static async googleAuth(): Promise<ApiResponse<{ redirectUrl: string }>> {
    // For Google Auth, we need to redirect to the actual URL
    const base =
      (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';
    const redirectUrl = `${base}/api/integrations/google`;
    return Promise.resolve({
      success: true,
      data: { redirectUrl },
    });
  }

  // Cases endpoints
  static async getCases(
    page = 1,
    limit = 20
  ): Promise<PaginatedResponse<Case>> {
    const raw = await apiClient.get(`/case?page=${page}&pageSize=${limit}`);
    // New backend shape: { success, meta: {page,pageSize,total,totalPages,hasNextPage,hasPrevPage}, items: [...] }
    if (raw && raw.items && raw.meta) {
      return {
        data: raw.items,
        total: raw.meta.total,
        page: raw.meta.page,
        limit: raw.meta.pageSize,
        totalPages: raw.meta.totalPages,
        hasNextPage: raw.meta.hasNextPage,
        hasPrevPage: raw.meta.hasPrevPage,
      };
    }
    // Fallback to old shape
    return raw;
  }

  static async getCase(caseId: string): Promise<ApiResponse<Case>> {
    return apiClient.get(`/case/${caseId}`);
  }

  static async createCase(
    caseData: CreateCaseForm
  ): Promise<CreateCaseResponse> {
    return apiClient.post('/case', caseData);
  }

  static async updateCase(
    caseId: string,
    caseData: Partial<Case>
  ): Promise<ApiResponse<Case>> {
    return apiClient.put(`/case/${caseId}`, caseData);
  }

  static async deleteCase(caseId: string): Promise<ApiResponse> {
    return apiClient.delete(`/case/${caseId}`);
  }

  // Documents endpoints
  static async getCaseDocuments(
    caseId: string,
    page = 1,
    pageSize = 20
  ): Promise<any> {
    const raw = await apiClient.get(
      `/case/${caseId}/documents?page=${page}&pageSize=${pageSize}`
    );
    // Expected new shape: { success, meta, items }
    if (raw && raw.items && raw.meta) {
      return raw; // consumer will interpret
    }
    // Fallback legacy (array)
    return {
      success: true,
      meta: {
        page,
        pageSize,
        total: raw?.length || 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      },
      items: raw || [],
    };
  }

  static async uploadDocument(
    caseId: string,
    file: File
  ): Promise<ApiResponse<Document>> {
    const formData = new FormData();
    formData.append('files', file); // Changed from 'file' to 'files'

    return apiClient.post(`/case/${caseId}/documents/upload`, formData);
  }

  static async uploadDocuments(caseId: string, files: File[]): Promise<any> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    const raw = await apiClient.post(
      `/case/${caseId}/documents/upload`,
      formData
    );
    // New shape: { success, documents: [...] }
    return raw;
  }

  static async deleteDocument(
    caseId: string,
    documentId: string
  ): Promise<ApiResponse> {
    return apiClient.delete(`/case/${caseId}/documents/${documentId}`);
  }

  static async renameDocument(
    caseId: string,
    documentId: string,
    customFileName: string
  ): Promise<ApiResponse<Document>> {
    return apiClient.patch(`/case/${caseId}/documents/${documentId}/rename`, {
      customFileName,
    });
  }

  // DEPRECATED: Exhibit numbers should only be managed through exhibit lists
  // This method has been removed as the corresponding backend endpoint is disabled
  // static async updateDocumentExhibitNumber(
  //   caseId: string,
  //   documentId: string,
  //   exhibitNumber: string,
  //   exhibitListId?: string
  // ): Promise<ApiResponse<Document>> {
  //   return apiClient.patch(
  //     `/case/${caseId}/documents/${documentId}/exhibit-number`,
  //     {
  //       exhibitNumber,
  //       exhibitListId,
  //     }
  //   );
  // }

  static async retryDocument(documentId: string): Promise<ApiResponse> {
    // TODO: Update this endpoint when it's ready
    return apiClient.post('/monitoring/reindex-documents', {
      documentIds: [documentId],
      workflowType: 'case',
    });
  }

  static async getCaseAnalysis(caseId: string): Promise<any> {
    // Expected shape:
    // { success: true, data: { caseId, visaType, overallStrength, readinessScore, stage, criteriaAnalysis: [{criteriaId,criteriaName,matchScore,satisfactionLevel}], estimatedTimeToReady } }
    const raw = await apiClient.get(
      `/case/criteria/${caseId}/criteria-analysis`
    );
    if (raw && raw.data) {
      return {
        success: true,
        data: {
          strengthAnalysis: {
            caseId: raw.data.caseId,
            visaType: raw.data.visaType,
            overallStrength: raw.data.overallStrength,
            readinessScore: raw.data.readinessScore,
            stage: raw.data.stage,
            criteriaAnalysis: raw.data.criteriaAnalysis || [],
            estimatedTimeToReady: raw.data.estimatedTimeToReady,
          },
        },
      } as any;
    }
    return raw;
  }

  static async getDocument(documentId: string): Promise<ApiResponse<Document>> {
    return apiClient.get(`/documents/${documentId}`);
  }

  static async updateDocumentCategory(
    documentId: string,
    category: string
  ): Promise<ApiResponse<Document>> {
    return apiClient.put(`/documents/${documentId}/category`, { category });
  }

  // Letters endpoints
  static async getCaseLetters(caseId: string): Promise<ApiResponse<Letter[]>> {
    return apiClient.get(`/case/${caseId}/letters`);
  }

  static async getLetter(letterId: string): Promise<ApiResponse<Letter>> {
    return apiClient.get(`/letters/${letterId}`);
  }

  static async generateLetter(
    caseId: string,
    type: string,
    prompt?: string
  ): Promise<ApiResponse<Letter>> {
    return apiClient.post(`/case/${caseId}/letters/generate`, {
      type,
      prompt,
    });
  }

  static async updateLetter(
    letterId: string,
    content: string
  ): Promise<ApiResponse<Letter>> {
    return apiClient.put(`/letters/${letterId}`, { content });
  }

  static async deleteLetter(letterId: string): Promise<ApiResponse> {
    return apiClient.delete(`/letters/${letterId}`);
  }

  // Stats and monitoring
  static async getProcessingStats(
    caseId?: string
  ): Promise<ApiResponse<ProcessingStats>> {
    const url = caseId
      ? `/stats/processing?caseId=${caseId}`
      : '/stats/processing';
    return apiClient.get(url);
  }

  static async getQueueStatus(): Promise<any> {
    return apiClient.get('/queue/status');
  }

  // Search
  static async searchDocuments(
    query: string,
    caseId?: string
  ): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams({ query });
    if (caseId) params.append('caseId', caseId);
    return apiClient.get(`/search/documents?${params.toString()}`);
  }

  static async semanticSearch(
    query: string,
    caseId?: string
  ): Promise<ApiResponse<any[]>> {
    return apiClient.post('/search/semantic', { query, caseId });
  }

  // AI Analysis
  static async analyzeDocument(documentId: string): Promise<any> {
    return apiClient.post(`/ai/analyze-document/${documentId}`, {});
  }

  static async generateSummary(
    documentId: string
  ): Promise<ApiResponse<string>> {
    return apiClient.post(`/ai/generate-summary/${documentId}`, {});
  }

  static async categorizeDocument(
    documentId: string
  ): Promise<ApiResponse<string>> {
    return apiClient.post(`/ai/categorize-document/${documentId}`, {});
  }

  // Visa types
  static async fetchVisaTypes(force = false): Promise<string[]> {
    if (!force && _visaTypesCache) return _visaTypesCache;
    if (!force && _visaTypesPromise) return _visaTypesPromise;

    const staticFallback = [
      'O-1A',
      'O-1B',
      'EB-1A',
      'EB-1B',
      'EB-1C',
      'EB-2',
      'EB-2-NIW',
      'L-1A',
      'L-1B',
      'H-1B',
    ];

    _visaTypesPromise = apiClient
      .get('/immigration/visa-types')
      .then((res: ApiResponse<string[]>) => {
        if (res.success && Array.isArray(res.data) && res.data.length) {
          _visaTypesCache = res.data;
        } else {
          _visaTypesCache = staticFallback;
        }
        return _visaTypesCache;
      })
      .catch(() => {
        _visaTypesCache = staticFallback;
        return _visaTypesCache;
      })
      .finally(() => {
        _visaTypesPromise = null;
      });

    return _visaTypesPromise!;
  }

  // Folder suggestions for organizing documents
  static async getFolderSuggestions(
    caseId?: string
  ): Promise<FolderSuggestionResponse> {
    if (caseId) {
      return apiClient.get(
        `/documents/analysis/folder-suggestions?caseId=${caseId}`
      );
    }
    return apiClient.get(`/documents/analysis/folder-suggestions`);
  }

  // Folder management operations
  static async getFolderTree(
    caseId: string,
    includeEmpty = false
  ): Promise<any> {
    const params = includeEmpty ? '?includeEmpty=true' : '';
    return apiClient.get(`/case/${caseId}/folders${params}`);
  }

  static async createFolder(
    caseId: string,
    name: string,
    parentId?: string
  ): Promise<any> {
    return apiClient.post(`/case/${caseId}/folders`, { name, parentId });
  }

  static async renameFolder(
    caseId: string,
    folderId: string,
    name: string
  ): Promise<any> {
    return apiClient.patch(`/case/${caseId}/folders/${folderId}`, { name });
  }

  static async moveFolder(
    caseId: string,
    folderId: string,
    newParentId?: string | null
  ): Promise<any> {
    return apiClient.patch(`/case/${caseId}/folders/${folderId}/move`, {
      newParentId,
    });
  }

  static async deleteFolder(
    caseId: string,
    folderId: string,
    options?: { cascade?: boolean; hard?: boolean }
  ): Promise<any> {
    const params = new URLSearchParams();
    if (options?.cascade) params.append('cascade', 'true');
    if (options?.hard) params.append('hard', 'true');
    const query = params.toString();
    return apiClient.delete(
      `/case/${caseId}/folders/${folderId}${query ? `?${query}` : ''}`
    );
  }

  static async restoreFolder(caseId: string, folderId: string): Promise<any> {
    return apiClient.put(`/case/${caseId}/folders/${folderId}/restore`, {});
  }

  /**
   * AI-powered folder reorganization
   * Merges duplicate folders, reassigns documents, cleans up empty folders
   */
  static async reorganizeFolders(caseId: string): Promise<any> {
    return apiClient.post(`/case/${caseId}/folders/reorganize`, {});
  }

  static async moveFile(
    caseId: string,
    documentId: string,
    targetFolderId?: string | null
  ): Promise<any> {
    return apiClient.post(`/case/${caseId}/folders/move-file`, {
      documentId,
      targetFolderId,
    });
  }

  // Manual document-criteria assignment
  static async assignDocumentToCriteria(
    caseId: string,
    documentId: string,
    criteriaId: string,
    notes?: string
  ): Promise<any> {
    return apiClient.post(
      `/case/${caseId}/documents/${documentId}/criteria/${criteriaId}/assign`,
      { notes }
    );
  }

  static async unassignDocumentFromCriteria(
    caseId: string,
    documentId: string,
    criteriaId: string
  ): Promise<any> {
    return apiClient.delete(
      `/case/${caseId}/documents/${documentId}/criteria/${criteriaId}/unassign`
    );
  }

  static async getDocumentCriteriaAssignments(
    caseId: string,
    documentId: string
  ): Promise<ApiResponse<any[]>> {
    return apiClient.get(
      `/case/${caseId}/documents/${documentId}/criteria-assignments`
    );
  }

  static async getAvailableCriteria(
    caseId: string
  ): Promise<ApiResponse<any[]>> {
    return apiClient.get(`/case/${caseId}/documents/criteria/available`);
  }

  // Exhibit Lists
  static async getExhibitLists(caseId: string): Promise<ApiResponse<any[]>> {
    return apiClient.get(`/case/${caseId}/exhibit-lists`);
  }

  static async getExhibitList(caseId: string, exhibitListId: string): Promise<ApiResponse<any>> {
    return apiClient.get(`/case/${caseId}/exhibit-lists/${exhibitListId}`);
  }

  static async generateExhibitList(
    caseId: string,
    options?: {
      maxDocuments?: number;
      minScore?: number;
      excludeCriteria?: string[];
      customPrompt?: string;
      format?: 'standard' | 'detailed' | 'condensed';
      formatType?: 'TABLE_OF_CONTENTS' | 'EXHIBIT_TABS' | 'PAGE_NUMBER_INDICATION' | 'COMBINED';
      numberingScheme?: 'NUMERIC' | 'ALPHA_UPPER' | 'ALPHA_LOWER' | 'ROMAN_UPPER' | 'ROMAN_LOWER' | 'HIERARCHICAL';
      includeCoverPages?: boolean;
      coverPageStyle?: 'SIMPLE' | 'DETAILED';
      includeTableOfContents?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    // Build query parameters instead of sending as POST body
    const params = new URLSearchParams();
    if (options?.maxDocuments) params.append('maxDocuments', String(options.maxDocuments));
    if (options?.minScore) params.append('minRelevanceScore', String(options.minScore));
    if (options?.customPrompt) params.append('customPrompt', options.customPrompt);
    if (options?.format) params.append('format', options.format);
    if (options?.formatType) params.append('formatType', options.formatType);
    if (options?.numberingScheme) params.append('numberingScheme', options.numberingScheme);
    if (options?.includeCoverPages !== undefined) params.append('includeCoverPages', String(options.includeCoverPages));
    if (options?.coverPageStyle) params.append('coverPageStyle', options.coverPageStyle);
    if (options?.includeTableOfContents !== undefined) params.append('includeTableOfContents', String(options.includeTableOfContents));

    const queryString = params.toString();
    const url = `/case/${caseId}/exhibit-lists/generate${queryString ? `?${queryString}` : ''}`;

    return apiClient.post(url, {});
  }

  static async deleteExhibitList(
    caseId: string,
    exhibitListId: string
  ): Promise<ApiResponse> {
    return apiClient.delete(`/case/${caseId}/exhibit-lists/${exhibitListId}`);
  }

  static async downloadExhibitList(
    caseId: string,
    exhibitListId: string,
    format: 'pdf' | 'docx' | 'markdown' = 'markdown',
    includeDocuments: boolean = false
  ): Promise<Blob> {
    const params = new URLSearchParams({ format });
    if (format === 'pdf' && includeDocuments) {
      params.append('includeDocuments', 'true');
    }

    // Use apiClient.getBlob() to ensure proper auth token is used
    return apiClient.getBlob(
      `/case/${caseId}/exhibit-lists/${exhibitListId}/download?${params.toString()}`
    );
  }

  // Google Drive Integration
  static async importFromGoogleDrive(
    caseId: string,
    options: {
      fileId: string;
      fileName: string;
      mimeType?: string;
    }
  ): Promise<ApiResponse<Document>> {
    // Use the scan endpoint to import a single file from Google Drive
    return apiClient.post('/documents/processing/scan/folders', {
      fileIds: [options.fileId],
      folderIds: [],
      options: {
        caseId,
        fileName: options.fileName,
        mimeType: options.mimeType,
      }
    });
  }

  static async importMultipleFromGoogleDrive(
    caseId: string,
    files: Array<{ fileId: string; fileName: string; mimeType?: string }>
  ): Promise<ApiResponse<{ scanId: string }>> {
    // Use the scan endpoint to import multiple files from Google Drive
    return apiClient.post('/documents/processing/scan/folders', {
      fileIds: files.map(f => f.fileId),
      folderIds: [],
      options: {
        caseId,
      }
    });
  }

  static async importFolderFromGoogleDrive(
    caseId: string,
    folderId: string,
    options?: {
      recursive?: boolean;
      includeShared?: boolean;
      fileTypes?: string[];
    }
  ): Promise<ApiResponse<{ scanId: string }>> {
    return apiClient.post('/documents/processing/scan/folders', {
      folderIds: [folderId],
      fileIds: [],
      options: {
        caseId,
        recursive: options?.recursive ?? true,
        includeShared: options?.includeShared ?? true,
        fileTypes: options?.fileTypes,
      }
    });
  }

  static async getScanStatus(scanId: string): Promise<ApiResponse<any>> {
    return apiClient.get(`/documents/processing/scan/status/${scanId}`);
  }

  static async getScanResults(scanId: string): Promise<ApiResponse<any>> {
    return apiClient.get(`/documents/processing/scan/results/${scanId}`);
  }

  // Google Drive browsing
  static async getGoogleDriveRootContents(): Promise<ApiResponse<any>> {
    return apiClient.get('/documents/drive/files/');
  }

  static async getGoogleDriveFolderContents(folderId: string): Promise<ApiResponse<any>> {
    return apiClient.get(`/documents/drive/folders/${folderId}/contents`);
  }

  static async searchGoogleDrive(
    query: string,
    type: 'files' | 'folders' | 'all' = 'all'
  ): Promise<ApiResponse<any>> {
    return apiClient.get(`/documents/drive/files/search?q=${encodeURIComponent(query)}&type=${type}`);
  }

  // Document download options
  static async downloadExtractedText(
    caseId: string,
    documentId: string,
    format: 'txt' | 'json' = 'txt'
  ): Promise<string> {
    return apiClient.get(
      `/case/${caseId}/documents/${documentId}/extracted-text?format=${format}`
    );
  }

  static async downloadTranslatedText(
    caseId: string,
    documentId: string,
    format: 'txt' | 'md' = 'md'
  ): Promise<string> {
    return apiClient.get(
      `/case/${caseId}/documents/${documentId}/translated-text?format=${format}`
    );
  }
}

export default ApiService;
